import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import { MAX_PAGE_SIZE } from "../../utils/constants/constants";
import { Field, inputClass } from "../../components/forms/Field";
import FilterSelect from "../../components/forms/FilterSelect";
import MultiSelect from "../../components/forms/MultiSelect";
import PanelHeader from "../../components/timetable/PanelHeader";
import {
  BTN,
  Card,
  EmptyState,
  ErrorState,
  Panel,
  PanelToolbar,
  Refreshing,
  STALE,
  SearchInput,
  TABLE_HEAD,
  TableSkeleton,
} from "../../components/timetable/PanelKit";
import ModalShell from "../Templates/ModalShell";
import ConfirmModal from "../Templates/ConfirmModal";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { compareText } from "../../utils/helpers/locale";
import { getDomainSubjects } from "../../services/organizationService";
import {
  createRoom,
  deleteRoom,
  getRooms,
  updateRoom,
} from "../../services/schedulingService";

// ---------------------------------------------------------------------------
// Rooms.
//
// The single most important thing on this page is the empty state, and the
// reason is counter-intuitive: a school that dutifully types in all 30 of its
// classrooms makes the timetable generator slower and gains nothing. A class
// that stays in its own room cannot clash with itself, so those rooms carry no
// information. Only the spaces lessons COMPETE for — the labs, the gym, the
// music room — are worth listing, so the page has to say that before it offers
// an "Add room" button.
//
// The list is expected to hold a handful of rows, so it is fetched in one page
// and filtered in the browser rather than paginated.
// ---------------------------------------------------------------------------

const rowsOf = (data) => (Array.isArray(data) ? data : data?.results || []);

// `subjects` is a uuid list on the write side; tolerate a nested read shape too.
const uuidOf = (value) => (typeof value === "string" ? value : value?.uuid);

export default function RoomsPage() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  // Rooms belong to one school; writes are own-school only.
  const orgUuid = user?.organizations?.[0]?.organization?.uuid;

  const [rooms, setRooms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [search, setSearch] = useState("");
  // ONE filter for the "Open to" column, not two. Shared-vs-subject is how the
  // model splits it (`is_shared` and the `subjects` M2M are separate fields),
  // but the question a manager asks is a single one — "which rooms can take
  // Chemistry?" — and the column they read it off is a single one too. Two
  // controls for one column would also allow the contradiction "shared AND
  // Chemistry-only", which by definition matches nothing.
  const [openTo, setOpenTo] = useState("");
  const debouncedSearch = useDebouncedValue(search, 200);

  const [editing, setEditing] = useState(null); // room | "new" | null
  const [deleting, setDeleting] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    getDomainSubjects({ signal: controller.signal })
      .then((data) => setSubjects(rowsOf(data)))
      .catch(() => setSubjects([]));
    return () => controller.abort();
  }, []);

  const loadRooms = useCallback(
    (signal) => {
      setIsLoading(true);
      getRooms({
        params: { organization: orgUuid || undefined, page_size: MAX_PAGE_SIZE },
        signal,
      })
        .then((data) => {
          setRooms(rowsOf(data));
          setLoadError(false);
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          // No toast: the failure renders as the panel below (first load) or
          // the strip above the table (refetch) — a toast would just repeat it.
          setLoadError(true);
        })
        .finally(() => setIsLoading(false));
    },
    [orgUuid],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadRooms(controller.signal);
    return () => controller.abort();
  }, [loadRooms]);

  const subjectNameByUuid = useMemo(
    () => Object.fromEntries(subjects.map((s) => [s.uuid, s.name])),
    [subjects],
  );
  const subjectOptions = useMemo(
    () => subjects.map((s) => ({ value: s.uuid, label: s.name })),
    [subjects],
  );

  // Only the subjects some room is actually set up for. Offering the whole
  // catalogue would be ~20 options, all but two of which match nothing — this
  // list is meant to be a handful of labs and a gym.
  const openToOptions = useMemo(() => {
    const hasShared = rooms.some((room) => room.is_shared);
    const subjectUuids = new Set();
    rooms.forEach((room) =>
      (room.subjects || []).map(uuidOf).filter(Boolean).forEach((uuid) => subjectUuids.add(uuid)),
    );
    return [
      ...(hasShared ? [{ value: "shared", label: t("timetable.rooms.sharedChip") }] : []),
      ...[...subjectUuids]
        .map((uuid) => ({
          value: `subject:${uuid}`,
          label: subjectNameByUuid[uuid] || t("timetable.rooms.unknownSubject"),
        }))
        .sort((a, b) => compareText(a.label, b.label)),
    ];
  }, [rooms, subjectNameByUuid, t]);

  const visibleRooms = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return rooms.filter((room) => {
      if (openTo === "shared" && !room.is_shared) return false;
      if (openTo.startsWith("subject:")) {
        const wanted = openTo.slice("subject:".length);
        if (!(room.subjects || []).map(uuidOf).includes(wanted)) return false;
      }
      if (!q) return true;
      return [room.name, room.code, room.building]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  }, [rooms, debouncedSearch, openTo]);

  const isFiltered = Boolean(debouncedSearch || openTo);
  const clearFilters = useCallback(() => {
    setSearch("");
    setOpenTo("");
  }, []);

  const confirmDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await deleteRoom({ uuid: deleting.uuid });
      toast.success(t("timetable.rooms.deleted"));
      setDeleting(null);
      loadRooms();
    } catch (err) {
      toast.error(formatApiError(err, t("timetable.rooms.deleteFailed")));
    } finally {
      setIsDeleting(false);
    }
  };

  // Page wrapper, back link, WIDTH and permission guard live on
  // TimetableWorkspace; rhythm and surfaces come from PanelKit.
  return (
    <>
      <Panel>
        <PanelHeader
          title={t("timetable.rooms.heading")}
          count={visibleRooms.length}
          subtitle={t("timetable.rooms.intro")}
        >
          {rooms.length > 0 && (
            <button
              type="button"
              onClick={() => setEditing("new")}
              className={BTN.primary}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              {t("timetable.rooms.add")}
            </button>
          )}
        </PanelHeader>

        {rooms.length > 0 && (
          <PanelToolbar>
            <SearchInput
              value={search}
              onChange={setSearch}
              ariaLabel={t("timetable.rooms.searchAria")}
            />
            {openToOptions.length > 1 && (
              <FilterSelect
                options={[
                  { value: "", label: t("timetable.rooms.allRooms") },
                  ...openToOptions,
                ]}
                value={openTo}
                onChange={setOpenTo}
                ariaLabel={t("timetable.rooms.filterOpenTo")}
                placeholder={t("timetable.rooms.allRooms")}
                minWidthClass="min-w-[13rem]"
              />
            )}
            {isFiltered && (
              <button type="button" onClick={clearFilters} className={BTN.quiet}>
                {t("common.clearFilters")}
              </button>
            )}
            {isLoading && (
              <span className="ml-auto">
                <Refreshing />
              </span>
            )}
          </PanelToolbar>
        )}

        {isLoading && rooms.length === 0 ? (
          <TableSkeleton
            rows={4}
            columns={[
              "w-40",
              "w-14",
              "w-24 hidden md:block",
              "w-16 hidden lg:block",
              "w-32",
            ]}
          />
        ) : loadError && rooms.length === 0 ? (
          <ErrorState
            title={t("timetable.rooms.loadFailed")}
            onRetry={() => loadRooms()}
          />
        ) : rooms.length === 0 ? (
          <RoomsEmptyState onAdd={() => setEditing("new")} />
        ) : visibleRooms.length === 0 ? (
          <EmptyState
            iconPath="M21 21l-4.3-4.3m1.8-4.7a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"
            title={t("timetable.rooms.noMatches")}
            hint={t("timetable.rooms.noMatchesHint")}
            action={
              <button type="button" onClick={clearFilters} className={BTN.secondary}>
                {t("common.clearFilters")}
              </button>
            }
          />
        ) : (
          <>
            <Card padded={false}>
              {loadError && (
                <div
                  role="alert"
                  className="px-6 py-2.5 bg-rose-50 text-rose-700 text-[11px] font-black border-b border-rose-100"
                >
                  {t("timetable.rooms.refreshFailed")}
                </div>
              )}
              <div className={`overflow-x-auto ${isLoading ? STALE : ""}`}>
                <table className="w-full text-sm" aria-busy={isLoading}>
                  <thead>
                    <tr className={TABLE_HEAD}>
                      <th className="px-6 py-3">{t("timetable.rooms.colName")}</th>
                      <th className="px-6 py-3">{t("timetable.rooms.colCode")}</th>
                      <th className="px-6 py-3 hidden md:table-cell">
                        {t("timetable.rooms.colBuilding")}
                      </th>
                      <th className="px-6 py-3 hidden lg:table-cell">
                        {t("timetable.rooms.colCapacity")}
                      </th>
                      <th className="px-6 py-3">{t("timetable.rooms.colUsedBy")}</th>
                      <th className="px-6 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRooms.map((room) => (
                      <RoomRow
                        key={room.uuid}
                        room={room}
                        subjectNameByUuid={subjectNameByUuid}
                        onEdit={() => setEditing(room)}
                        onDelete={() => setDeleting(room)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* The over-listing warning has to survive the empty state, or a
                school that added three rooms keeps adding thirty. */}
            <p className="max-w-2xl text-xs leading-relaxed text-slate-500">
              <span className="font-bold text-slate-600">{t("timetable.rooms.tipTitle")}</span>{" "}
              {t("timetable.rooms.tip")}
            </p>
          </>
        )}
      </Panel>

      {editing && (
        <RoomFormModal
          room={editing === "new" ? null : editing}
          subjectOptions={subjectOptions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadRooms();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t("timetable.rooms.deleteTitle")}
          body={t("timetable.rooms.deleteBody", {
            room: deleting.name || deleting.code || "",
          })}
          confirmLabel={
            isDeleting ? t("timetable.rooms.deleting") : t("timetable.rooms.delete")
          }
          danger
          busy={isDeleting}
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}

// -- table row ---------------------------------------------------------------

function RoomRow({ room, subjectNameByUuid, onEdit, onDelete }) {
  const { t } = useTranslation();
  const subjectUuids = (room.subjects || []).map(uuidOf).filter(Boolean);

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-6 py-3 font-medium text-slate-900">{room.name || "—"}</td>
      <td className="px-6 py-3">
        {room.code ? (
          <span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-black uppercase tracking-wider">
            {room.code}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-6 py-3 text-slate-600 hidden md:table-cell">{room.building || "—"}</td>
      <td className="px-6 py-3 text-slate-600 hidden lg:table-cell">
        {room.capacity ? room.capacity : "—"}
      </td>
      <td className="px-6 py-3">
        <div className="flex flex-wrap gap-1">
          {room.is_shared && (
            <span className="inline-block px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-[11px] font-semibold">
              {t("timetable.rooms.sharedChip")}
            </span>
          )}
          {subjectUuids.map((uuid) => (
            <span
              key={uuid}
              className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-semibold"
            >
              {subjectNameByUuid[uuid] || t("timetable.rooms.unknownSubject")}
            </span>
          ))}
          {!room.is_shared && subjectUuids.length === 0 && (
            <span className="text-[11px] text-slate-500">
              {t("timetable.rooms.namedOnly")}
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-3">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="px-2 py-1 rounded-lg text-xs font-semibold text-indigo-600 hover:bg-indigo-50 transition"
          >
            {t("timetable.rooms.edit")}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="px-2 py-1 rounded-lg text-xs font-semibold text-rose-600 hover:bg-rose-50 transition"
          >
            {t("timetable.rooms.delete")}
          </button>
        </div>
      </td>
    </tr>
  );
}

// -- empty / loading ---------------------------------------------------------

// This is the teaching surface of the whole feature. "No rooms" is the RIGHT
// answer for most schools, so the empty state congratulates rather than nags,
// and spells out the one case that genuinely needs a row.
function RoomsEmptyState({ onAdd }) {
  const { t } = useTranslation();
  const examples = [
    t("timetable.rooms.emptyExample1"),
    t("timetable.rooms.emptyExample2"),
    t("timetable.rooms.emptyExample3"),
  ];

  return (
    <EmptyState
      iconPath="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5"
      title={t("timetable.rooms.empty")}
      hint={t("timetable.rooms.emptyLead")}
      action={
        <button type="button" onClick={onAdd} className={BTN.primary}>
          {t("timetable.rooms.emptyAction")}
        </button>
      }
    >
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {t("timetable.rooms.emptyExamplesTitle")}
        </p>
        <ul className="mt-2 space-y-1.5">
          {examples.map((example) => (
            <li key={example} className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
              <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              {example}
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-4 text-center text-xs leading-relaxed text-slate-500">
        {t("timetable.rooms.emptyWarning")}
      </p>
    </EmptyState>
  );
}

// -- create / edit modal -----------------------------------------------------

function RoomFormModal({ room, subjectOptions, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = Boolean(room);

  const [name, setName] = useState(room?.name || "");
  const [code, setCode] = useState(room?.code || "");
  const [building, setBuilding] = useState(room?.building || "");
  const [capacity, setCapacity] = useState(
    room?.capacity == null ? "" : String(room.capacity),
  );
  const [isShared, setIsShared] = useState(Boolean(room?.is_shared));
  const [subjects, setSubjects] = useState(
    (room?.subjects || []).map(uuidOf).filter(Boolean),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = t("timetable.roomForm.errName");
    if (capacity !== "" && (!Number.isInteger(Number(capacity)) || Number(capacity) < 1)) {
      next.capacity = t("timetable.roomForm.errCapacity");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      const body = {
        name: name.trim(),
        code: code.trim(),
        building: building.trim(),
        capacity: capacity === "" ? null : Number(capacity),
        is_shared: isShared,
        subjects,
      };
      if (isEdit) {
        await updateRoom({ uuid: room.uuid, body });
        toast.success(t("timetable.rooms.updated"));
      } else {
        await createRoom({ body });
        toast.success(t("timetable.rooms.created"));
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, t("timetable.rooms.saveFailed")));
    } finally {
      setIsSaving(false);
    }
  };

  // max-w-2xl, matching the Lessons and Rules forms — this one is the same
  // two-column grid and was 96px narrower for no reason.
  return (
    <ModalShell onClose={onClose} busy={isSaving} maxWidth="max-w-2xl">
      <h3 className="text-lg font-bold text-slate-900 pr-8">
        {isEdit ? t("timetable.roomForm.editTitle") : t("timetable.roomForm.createTitle")}
      </h3>

      <form onSubmit={submit} noValidate className="mt-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("timetable.roomForm.name")} error={errors.name} required>
            <input
              type="text"
              value={name}
              disabled={isSaving}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("timetable.roomForm.namePlaceholder")}
              className={inputClass(errors.name)}
            />
          </Field>

          <Field label={t("timetable.roomForm.code")}>
            <input
              type="text"
              value={code}
              disabled={isSaving}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t("timetable.roomForm.codePlaceholder")}
              className={inputClass(false)}
            />
            <p className="mt-1 text-xs text-slate-400">{t("timetable.roomForm.codeHint")}</p>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("timetable.roomForm.building")}>
            <input
              type="text"
              value={building}
              disabled={isSaving}
              onChange={(e) => setBuilding(e.target.value)}
              className={inputClass(false)}
            />
          </Field>

          <Field label={t("timetable.roomForm.capacity")} error={errors.capacity}>
            <input
              type="number"
              min="1"
              step="1"
              value={capacity}
              disabled={isSaving}
              onChange={(e) => setCapacity(e.target.value)}
              className={inputClass(errors.capacity)}
            />
            <p className="mt-1 text-xs text-slate-400">{t("timetable.roomForm.capacityHint")}</p>
          </Field>
        </div>

        {/* -- who may be sent here ----------------------------------------- */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            {t("timetable.roomForm.useTitle")}
          </p>
          <p className="mt-1 text-xs text-slate-400 leading-relaxed">
            {t("timetable.roomForm.useHint")}
          </p>

          <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={isShared}
              disabled={isSaving}
              onChange={(e) => setIsShared(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500/30"
            />
            <span>
              <span className="block text-sm font-semibold text-slate-700">
                {t("timetable.roomForm.shared")}
              </span>
              <span className="block text-xs text-slate-400 leading-relaxed">
                {t("timetable.roomForm.sharedHint")}
              </span>
            </span>
          </label>

          <div className="mt-4">
            <Field label={t("timetable.roomForm.subjects")}>
              <MultiSelect
                options={subjectOptions}
                value={subjects}
                onChange={setSubjects}
                disabled={isSaving}
                placeholder={t("timetable.roomForm.subjectsPlaceholder")}
                emptyText={t("timetable.roomForm.subjectsEmpty")}
              />
              <p className="mt-1 text-xs text-slate-400 leading-relaxed">
                {t("timetable.roomForm.subjectsHint")}
              </p>
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className={BTN.quiet}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className={BTN.primary}
          >
            {isSaving
              ? t("common.saving")
              : isEdit
                ? t("timetable.roomForm.save")
                : t("timetable.roomForm.create")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
